import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { Sidebar, Topbar } from './components/Shell.jsx';
import { Icon } from './components/Icons.jsx';
import { AIPanel, VoiceMode } from './components/AIAssistant.jsx';
import { useAuth } from './contexts/AuthContext.jsx';
import { Onboarding } from './screens/OnboardingChat.jsx';
import { Dashboard } from './screens/Dashboard.jsx';
import { Library } from './screens/Library.jsx';
import { PostPreview } from './screens/PostPreview.jsx';
import { EditPost } from './screens/EditPost.jsx';
import { Settings as NewSettings } from './screens/Settings.jsx';
import { CreatePost as NewCreatePost } from './screens/CreatePost.jsx';
import { ContentPlan as NewContentPlan } from './screens/ContentPlan.jsx';
import { EditStrategy as NewEditStrategy } from './screens/EditStrategy.jsx';
import { Calendar as NewCalendar } from './screens/Calendar.jsx';
import { Batch as NewBatch } from './screens/Batch.jsx';
import { Messages as NewMessages } from './screens/Messages.jsx';
import { Auth as NewAuth } from './screens/Auth.jsx';
import { Referrals as NewReferrals } from './screens/Referrals.jsx';
import { ReferralSignup } from './screens/ReferralSignup.jsx';
import { ReferralWelcome } from './screens/ReferralWelcome.jsx';
import { AdminReferrals } from './screens/AdminReferrals.jsx';
import { AppShell } from './components/AppShell';
import { Scheduler } from './screens/Scheduler.jsx';
import { Content } from './screens/Content.jsx';
import { Create } from './screens/Create.jsx';
import { CarouselEditor } from './screens/Carousel.jsx';
import { HelpCenter, NotificationsPanel, NotFound } from './screens/Support.jsx';
import { Billing, Settings } from './screens/DesignScreens.jsx';
import { ContentPlanScreen } from './screens/content-plan/ContentPlanScreen.jsx';
import { api } from './services/api/client.js';
import { ContentFeedProvider } from './state/ContentFeedContext.jsx';
import { DashboardDataProvider } from './state/DashboardDataContext.jsx';
import { useContentFeed } from './hooks/useContentFeed.js';
import {
  PostEditor,
  PostPreview as LegacyPostPreview,
  SocialAccounts
} from './screens/Misc.jsx';

const SCREEN_PATHS = {
  onboarding: '/onboarding',
  dashboard: '/app',
  scheduler: '/app/calendar',
  content: '/app/library',
  create: '/app/create',
  preview: '/app/preview',
  social: '/app/settings?tab=social',
  brand: '/app/plan',
  billing: '/app/settings?tab=billing',
  help: '/app/help',
  settings: '/app/settings'
};

const ACTIVATION_RETURN_KEY = 'activation_return_to_first_post';
const ONBOARDING_SOCIAL_RETURN_KEY = 'dathent_onboarding_social_oauth_return';
const ASSISTANT_SOCIAL_RETURN_KEY = 'dathent_assistant_social_oauth_return';

const LEGACY_PATH_TO_SCREEN = {
  scheduler: 'scheduler',
  content: 'content',
  create: 'create',
  brand: 'brand',
  social: 'settings',
  billing: 'settings',
  dashboard: 'dashboard',
  preview: 'preview',
  help: 'help',
  settings: 'settings',
  // Pack route names (flat /app/<name>)
  calendar: 'scheduler',
  library: 'content',
  plan: 'brand',
  notifications: 'help',
  account: 'settings',
};

const SETTINGS_TAB_FOR_LEGACY_SCREEN = {
  billing: 'billing',
  social: 'social',
};

function screenFromLocation(location) {
  const params = new URLSearchParams(location.search);
  const normalizedPath = location.pathname.replace(/^\/app(?=\/|$)/, '') || '/';
  if (normalizedPath.startsWith('/settings/integrations') || (normalizedPath === '/' && params.has('provider'))) {
    return 'settings';
  }

  const segments = normalizedPath.replace(/^\/+/, '').split('/');
  const [firstSegment, contentId, contentAction] = segments;
  if (firstSegment === '') return null;
  if (firstSegment === 'onboarding') return 'onboarding';
  if (firstSegment === 'content' && contentId && contentAction === 'preview') return 'preview';
  if (firstSegment === 'content' && contentId && contentAction === 'edit') return 'editor';
  if (firstSegment === 'content' && contentId && contentAction === 'carousel') return 'carousel';
  if (firstSegment === 'posts') {
    if (contentId === 'create') return 'create';
    if (contentId === 'library') return 'content';
    if (contentId === 'calendar') return 'scheduler';
    return 'notFound';
  }
  if (firstSegment === 'content-plan') return 'brand';
  return LEGACY_PATH_TO_SCREEN[firstSegment] || 'notFound';
}

function contentIdFromLocation(location) {
  const normalizedPath = location.pathname.replace(/^\/app(?=\/|$)/, '') || '/';
  const [firstSegment, contentId, contentAction] = normalizedPath.replace(/^\/+/, '').split('/');
  return firstSegment === 'content' && contentId && ['preview', 'edit', 'carousel'].includes(contentAction) ? decodeURIComponent(contentId) : null;
}

function LoadingScreen() {
  return (
    <div className="onboard-page">
      <div className="card" style={{width:'min(420px, calc(100vw - 32px))', textAlign:'center'}}>
        <div className="ai-orb" style={{margin:'0 auto 16px'}}/>
        <h2 className="h2" style={{marginBottom:8}}>Loading Dathent</h2>
        <div className="muted">Checking your session...</div>
      </div>
    </div>
  );
}

function AuthAside({ heading, sub }) {
  return (
    <aside className="auth-aside">
      <div className="auth-aside-bg" />
      <div className="auth-orb" />
      <div className="auth-aside-content auth-brand">
        <div className="auth-logo">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 2 4 7v10l8 5 8-5V7zM4 7l8 5 8-5M12 22V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <span>Dathent</span>
      </div>
      <div className="auth-aside-content" style={{ maxWidth: 380 }}>
        <div className="eyebrow" style={{ color: 'var(--p-200)', marginBottom: 12 }}>AI Growth Autopilot</div>
        <h2 className="h1" style={{ fontSize: 34, marginBottom: 12 }}>{heading || 'Turn content into sales growth on autopilot'}</h2>
        <p className="body" style={{ color: 'var(--text-2)', margin: 0 }}>{sub || 'Dathent builds your strategy, generates posts, and publishes to connected social channels automatically.'}</p>
      </div>
    </aside>
  );
}

function AuthScreen({ mode }) {
  const isSignup = mode === 'signup';
  const { login, verifyMfaLogin, verifyMfaBackupCode, sendMfaEmailCode, verifyMfaEmailCode, verifyRequiredMfaSetup, reloadUser, isAuthenticated, loading, error } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaBackupCode, setMfaBackupCode] = useState('');
  const [mfaEmailCode, setMfaEmailCode] = useState('');
  const [mfaEmailSent, setMfaEmailSent] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [mfaSetupChallenge, setMfaSetupChallenge] = useState(null);
  const [mfaSetupQrDataUrl, setMfaSetupQrDataUrl] = useState(null);
  const [mfaSetupCode, setMfaSetupCode] = useState('');
  const [mfaSetupBackupCodes, setMfaSetupBackupCodes] = useState(null);
  const [mfaSetupCodesSaved, setMfaSetupCodesSaved] = useState(false);
  const [mfaMode, setMfaMode] = useState('totp');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState(null);
  const [mfaNotice, setMfaNotice] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      const from = location.state?.from?.pathname;
      navigate(from && !['/login', '/signup', '/forgot', '/reset', '/confirm'].includes(from) ? from : '/', { replace: true });
    }
  }, [isAuthenticated, loading, location.state, navigate]);

  useEffect(() => {
    setFormError(null);
    setMfaChallenge(null);
    setMfaSetupChallenge(null);
    setMfaSetupQrDataUrl(null);
    setMfaSetupCode('');
    setMfaSetupBackupCodes(null);
    setMfaSetupCodesSaved(false);
    setMfaCode('');
    setMfaBackupCode('');
    setMfaEmailCode('');
    setMfaEmailSent(false);
    setMfaMode('totp');
    setMfaNotice(null);
  }, [isSignup]);

  async function submit(event) {
    event.preventDefault();
    setFormError(null);

    if (mfaSetupChallenge) {
      if (mfaSetupBackupCodes) {
        if (!mfaSetupCodesSaved) {
          setFormError('Save your backup codes before entering the platform.');
          return;
        }
        await reloadUser();
        navigate('/', { replace: true });
        return;
      }
      if (!/^\d{6}$/.test(mfaSetupCode)) {
        setFormError('Enter a valid 6-digit code.');
        return;
      }
      try {
        setSubmitting(true);
        const result = await verifyRequiredMfaSetup(mfaSetupChallenge.attemptId, mfaSetupCode);
        setMfaSetupBackupCodes(result.backupCodes || []);
      } catch (err) {
        setFormError(err.message || 'Unable to enable two-factor authentication');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (mfaChallenge) {
      try {
        setSubmitting(true);
        if (mfaMode === 'backup') {
          await verifyMfaBackupCode(mfaChallenge.attemptId, mfaBackupCode.trim());
        } else if (mfaMode === 'email') {
          await verifyMfaEmailCode(mfaChallenge.attemptId, mfaEmailCode.trim());
        } else {
          await verifyMfaLogin(mfaChallenge.attemptId, mfaCode.trim());
        }
        navigate('/', { replace: true });
      } catch (err) {
        setFormError(err.message || 'Unable to verify two-factor code');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    setSubmitting(true);
    try {
      const result = await login(email.trim(), password);
      if (result?.requiresMfa) {
        setMfaChallenge(result);
        setMfaMode('totp');
        setMfaCode('');
        setMfaBackupCode('');
        setMfaEmailCode('');
        setMfaEmailSent(false);
        setMfaNotice(null);
        return;
      }
      if (result?.requiresMfaSetup) {
        setMfaSetupChallenge(result);
        setMfaSetupCode('');
        setMfaSetupBackupCodes(null);
        setMfaSetupCodesSaved(false);
        try {
          setMfaSetupQrDataUrl(await QRCode.toDataURL(result.otpauthUrl, { width: 196, margin: 1, color: { dark: '#111111', light: '#ffffff' } }));
        } catch {
          setMfaSetupQrDataUrl(null);
        }
        return;
      }
      navigate('/', { replace: true });
    } catch (err) {
      setFormError(err.message || 'Authentication failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function copyMfaSetupText(value) {
    await navigator.clipboard?.writeText(value);
  }

  function downloadMfaSetupBackupCodes() {
    const codes = mfaSetupBackupCodes || [];
    const blob = new Blob([codes.join('\n') + '\n'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'dathent-backup-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  }

  async function requestMfaEmailCode() {
    if (!mfaChallenge?.attemptId) return;
    try {
      setSubmitting(true);
      setFormError(null);
      setMfaNotice(null);
      await sendMfaEmailCode(mfaChallenge.attemptId);
      setMfaEmailSent(true);
      setMfaMode('email');
      setMfaEmailCode('');
      setMfaNotice(`We sent a login code to ${mfaChallenge.emailHint || 'your email'}.`);
    } catch (err) {
      setFormError(err.message || 'Unable to send email login code');
    } finally {
      setSubmitting(false);
    }
  }

  const mfaChallengeSubmitDisabled = submitting || loading || (
    mfaMode === 'totp'
      ? mfaCode.length !== 6
      : mfaMode === 'email'
        ? mfaEmailCode.length !== 6
        : !mfaBackupCode.trim()
  );

  return (
    <div className="auth-shell">
      <AuthAside
        heading={isSignup ? 'Private beta for growth teams' : undefined}
        sub={isSignup ? 'Public signup is paused while we onboard new workspaces manually.' : undefined}
      />
      <div className="auth-form-side cosmic-bg">
        <form className="auth-form-card slide-up" onSubmit={submit}>
          {mfaSetupChallenge ? (
            <>
              {!mfaSetupBackupCodes ? (
                <>
                  <h1 className="h1" style={{ marginBottom: 6 }}>Set up two-factor authentication</h1>
                  <p className="body" style={{ color: 'var(--text-3)', marginBottom: 24 }}>
                    Your workspace requires an authenticator app before you can enter Dathent.
                  </p>
                  {mfaSetupQrDataUrl ? <div className="mfa-qr" style={{ margin: '0 auto 16px' }}><img src={mfaSetupQrDataUrl} alt="Authenticator app QR code" /></div> : null}
                  {mfaSetupChallenge.manualKey && (
                    <div className="mfa-manual-key">
                      <div>
                        <div className="settings-section-label" style={{ margin: 0 }}>Can’t scan the QR code?</div>
                        <p className="caption" style={{ margin: '4px 0 10px' }}>Enter this setup key manually in your authenticator app.</p>
                      </div>
                      <code>{mfaSetupChallenge.manualKey}</code>
                      <button className="btn btn-secondary btn-sm" type="button" onClick={() => copyMfaSetupText(mfaSetupChallenge.manualKey)}>Copy setup key</button>
                    </div>
                  )}
                  <label className="label" style={{ marginTop: 16 }}>6-digit code</label>
                  <input className="input" inputMode="numeric" pattern="[0-9]*" value={mfaSetupCode} onChange={event => setMfaSetupCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" autoComplete="one-time-code" maxLength={6} required />
                  {(formError || error) && <div className="auth-error">{formError || error}</div>}
                  <button className="btn btn-primary btn-block btn-lg" type="submit" disabled={submitting || loading || mfaSetupCode.length !== 6} style={{ marginTop: 24 }}>
                    {submitting ? 'Verifying...' : 'Verify & enable 2FA'}
                  </button>
                  <button type="button" className="btn-ghost" style={{ background: 'none', border: 0, color: 'var(--text-3)', cursor: 'pointer', fontSize: 12, fontWeight: 600, marginTop: 18 }} onClick={() => { setMfaSetupChallenge(null); setMfaSetupCode(''); setMfaSetupBackupCodes(null); setFormError(null); }}>
                    Back to login
                  </button>
                </>
              ) : (
                <>
                  <h1 className="h1" style={{ marginBottom: 6 }}>Save your backup codes</h1>
                  <p className="body" style={{ color: 'var(--text-3)', marginBottom: 18 }}>
                    Store these codes somewhere safe. Each code works once if you lose your authenticator app.
                  </p>
                  <div className="mfa-backup-grid">{mfaSetupBackupCodes.map(codeValue => <code key={codeValue}>{codeValue}</code>)}</div>
                  <div className="row" style={{ gap: 8, marginTop: 14 }}>
                    <button className="btn btn-secondary btn-block" type="button" onClick={() => copyMfaSetupText(mfaSetupBackupCodes.join('\n'))}>Copy all codes</button>
                    <button className="btn btn-secondary btn-block" type="button" onClick={downloadMfaSetupBackupCodes}>Download codes</button>
                  </div>
                  <label className="row" style={{ gap: 8, marginTop: 18, alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input type="checkbox" checked={mfaSetupCodesSaved} onChange={event => setMfaSetupCodesSaved(event.target.checked)} style={{ accentColor: 'var(--p-500)', width: 16, height: 16, marginTop: 2 }} />
                    <span className="caption">I have saved these codes.</span>
                  </label>
                  {(formError || error) && <div className="auth-error">{formError || error}</div>}
                  <button className="btn btn-primary btn-block btn-lg" type="submit" disabled={!mfaSetupCodesSaved} style={{ marginTop: 18 }}>Enter Dathent</button>
                </>
              )}
            </>
          ) : mfaChallenge ? (
            <>
              <h1 className="h1" style={{ marginBottom: 6 }}>Two-factor authentication</h1>
              <p className="body" style={{ color: 'var(--text-3)', marginBottom: 28 }}>
                {mfaMode === 'backup'
                  ? 'Enter one of your saved backup codes.'
                  : mfaMode === 'email'
                    ? `Enter the 6-digit code sent to ${mfaChallenge.emailHint || 'your email'}.`
                    : 'Enter the 6-digit code from your authenticator app.'}
              </p>

              {mfaMode === 'backup' ? (
                <>
                  <label className="label">Backup code</label>
                  <input className="input" value={mfaBackupCode} onChange={event => setMfaBackupCode(event.target.value.toUpperCase())} placeholder="ABCD-1234" autoComplete="one-time-code" required />
                </>
              ) : mfaMode === 'email' ? (
                <>
                  <label className="label">Email code</label>
                  <input className="input" inputMode="numeric" pattern="[0-9]*" value={mfaEmailCode} onChange={event => setMfaEmailCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" autoComplete="one-time-code" maxLength={6} required />
                </>
              ) : (
                <>
                  <label className="label">Authenticator code</label>
                  <input className="input" inputMode="numeric" pattern="[0-9]*" value={mfaCode} onChange={event => setMfaCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" autoComplete="one-time-code" maxLength={6} required />
                </>
              )}

              {mfaNotice && <div className="auth-success" style={{ marginTop: 12 }}>{mfaNotice}</div>}
              {(formError || error) && <div className="auth-error">{formError || error}</div>}

              <button className="btn btn-primary btn-block btn-lg" type="submit" disabled={mfaChallengeSubmitDisabled} style={{ marginTop: 24 }}>
                {submitting ? 'Verifying...' : 'Verify'}
              </button>

              <div className="row between" style={{ marginTop: 18, gap: 12, flexWrap: 'wrap' }}>
                {mfaMode !== 'totp' && (
                  <button type="button" className="btn-ghost" style={{ background: 'none', border: 0, color: 'var(--p-300)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }} onClick={() => { setMfaMode('totp'); setFormError(null); setMfaNotice(null); }}>
                    Use authenticator code
                  </button>
                )}
                {mfaMode !== 'backup' && (
                  <button type="button" className="btn-ghost" style={{ background: 'none', border: 0, color: 'var(--p-300)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }} onClick={() => { setMfaMode('backup'); setFormError(null); setMfaNotice(null); }}>
                    Use backup code
                  </button>
                )}
                {mfaChallenge.emailCodeAvailable && (
                  <button type="button" className="btn-ghost" style={{ background: 'none', border: 0, color: 'var(--p-300)', cursor: submitting ? 'default' : 'pointer', fontSize: 12, fontWeight: 600 }} onClick={requestMfaEmailCode} disabled={submitting}>
                    {mfaEmailSent ? 'Resend email code' : 'Email me a login code'}
                  </button>
                )}
                <button type="button" className="btn-ghost" style={{ background: 'none', border: 0, color: 'var(--text-3)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }} onClick={() => { setMfaChallenge(null); setMfaCode(''); setMfaBackupCode(''); setMfaEmailCode(''); setMfaEmailSent(false); setMfaNotice(null); setFormError(null); }}>
                  Back to login
                </button>
              </div>
            </>
          ) : isSignup ? (
            <EarlyAccessSignup onLogin={() => navigate('/login')} />
          ) : (
            <>
          <h1 className="h1" style={{ marginBottom: 6 }}>Welcome back</h1>
          <p className="body" style={{ color: 'var(--text-3)', marginBottom: 28 }}>Log in to continue to your workspace.</p>

          <label className="label">Email</label>
          <input className="input" type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="you@company.com" autoComplete="email" required />

          <div className="row between" style={{ marginTop: 18, marginBottom: 8 }}>
            <label className="label" style={{ margin: 0 }}>Password</label>
            <button className="btn-ghost" type="button" style={{ background: 'none', border: 0, fontSize: 12, color: 'var(--p-300)', cursor: 'pointer' }} onClick={() => navigate('/forgot')}>
              Forgot password?
            </button>
          </div>
          <div style={{ position: 'relative' }}>
            <input className="input" type={showPassword ? 'text' : 'password'} value={password} onChange={event => setPassword(event.target.value)} placeholder="At least 8 characters" autoComplete="current-password" minLength={8} required />
            <button type="button" onClick={() => setShowPassword(value => !value)} aria-label="Toggle password" style={{ position: 'absolute', right: 10, top: 10, background: 'none', border: 0, color: 'var(--text-3)', padding: 6, cursor: 'pointer' }}>
              <Icon name={showPassword ? 'eyeOff' : 'eye'} size={16} />
            </button>
          </div>

          {(formError || error) && <div className="auth-error">{formError || error}</div>}

          <button className="btn btn-primary btn-block btn-lg" type="submit" disabled={submitting || loading} style={{ marginTop: 24 }}>
            {submitting ? 'Please wait...' : 'Continue'}
          </button>

          <div className="row" style={{ gap: 12, margin: '24px 0', color: 'var(--text-3)' }}>
            <div className="divider" style={{ flex: 1 }} />
            <span style={{ fontSize: 12 }}>email auth is live</span>
            <div className="divider" style={{ flex: 1 }} />
          </div>

          <p className="caption" style={{ textAlign: 'center', marginTop: 0 }}>
            Need access?{' '}
            <button type="button" onClick={() => navigate('/signup')} style={{ background: 'none', border: 0, color: 'var(--p-300)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              Request early access
            </button>
          </p>
            </>
          )}
        </form>
        <footer className="auth-foot">
          <a href="https://dathent.ai/privacy" target="_blank" rel="noopener noreferrer">Privacy</a>
          <span className="auth-foot-sep">·</span>
          <a href="https://dathent.ai/terms" target="_blank" rel="noopener noreferrer">Terms</a>
          <span className="auth-foot-sep">·</span>
          <a href="https://dathent.ai/security" target="_blank" rel="noopener noreferrer">Security</a>
        </footer>
      </div>
    </div>
  );
}

function EarlyAccessSignup({ onLogin }) {
  const earlyAccessFormUrl = 'https://forms.gle/7U4vLBGXRNM2Mv527';

  return (
    <>
      <h1 className="h1" style={{ marginBottom: 6 }}>Early access only</h1>
      <p className="body" style={{ color: 'var(--text-3)', marginBottom: 20 }}>
        New Dathent accounts are currently invite-only. Existing users can still log in normally.
      </p>
      <div className="auth-note">
        Need a workspace? Request early access and we will enable your account when onboarding is ready.
      </div>
      <a className="btn btn-primary btn-block btn-lg" href={earlyAccessFormUrl} target="_blank" rel="noopener noreferrer" style={{ marginTop: 24 }}>
        Request early access
      </a>
      <button className="btn btn-secondary btn-block" type="button" onClick={onLogin} style={{ marginTop: 12 }}>
        Back to login
      </button>
    </>
  );
}

function AuthUtilityScreen({ mode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(mode === 'confirm');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const resetToken = new URLSearchParams(location.search).get('token') || '';

  const content = {
    forgot: {
      title: sent ? 'Check your email' : 'Reset your password',
      body: sent ? `If ${email || 'your email'} exists, a reset link was sent.` : "Enter your email and we'll send a password reset link.",
      icon: 'mail',
    },
    reset: {
      title: sent ? 'Password updated' : 'Create new password',
      body: sent ? 'Your password was reset. Use the new password to log in.' : 'Choose a new password for your Dathent account.',
      icon: 'lock',
    },
    confirm: {
      title: 'Confirm your email',
      body: 'Open the confirmation link from your inbox, then return to Dathent.',
      icon: 'mail',
    },
  }[mode];

  async function submitForgot(event) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      await api.auth.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setFormError(err.message || 'Unable to send reset link');
    } finally {
      setSubmitting(false);
    }
  }

  async function submitReset(event) {
    event.preventDefault();
    setFormError(null);
    if (!resetToken) {
      setFormError('This reset link is missing a token. Request a new link.');
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await api.auth.resetPassword(resetToken, password);
      setSent(true);
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      setFormError(err.message || 'Unable to reset password');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-shell">
      <AuthAside />
      <div className="auth-form-side cosmic-bg">
        <div className="auth-form-card slide-up">
          <button className="btn btn-ghost btn-sm" style={{ padding: '6px 10px', marginBottom: 24, marginLeft: -10 }} onClick={() => navigate('/login')}>
            <Icon name="arrowLeft" size={14} /> Back to login
          </button>
          <div style={{ width: 56, height: 56, marginBottom: 20, borderRadius: 14, background: 'rgba(124,92,255,0.12)', border: '1px solid rgba(124,92,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--p-300)' }}>
            <Icon name={content.icon} size={24} />
          </div>
          <h1 className="h1" style={{ marginBottom: 6 }}>{content.title}</h1>
          <p className="body" style={{ color: 'var(--text-3)', marginBottom: 28 }}>{content.body}</p>

          {mode === 'forgot' && !sent && (
            <form onSubmit={submitForgot}>
              <label className="label">Email</label>
              <input className="input" type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="you@company.com" autoComplete="email" required />
              {formError && <div className="auth-error">{formError}</div>}
              <button className="btn btn-primary btn-block btn-lg" type="submit" disabled={submitting} style={{ marginTop: 24 }}>{submitting ? 'Sending...' : 'Send reset link'}</button>
            </form>
          )}

          {mode === 'reset' && !sent && (
            <form onSubmit={submitReset}>
              {!resetToken && <div className="auth-note">This reset link is missing a token. Request a new password reset email.</div>}
              <label className="label">New password</label>
              <input className="input" type="password" value={password} onChange={event => setPassword(event.target.value)} placeholder="At least 8 characters" minLength={8} autoComplete="new-password" required />
              <label className="label" style={{ marginTop: 18 }}>Confirm password</label>
              <input className="input" type="password" value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} placeholder="Re-enter password" minLength={8} autoComplete="new-password" required />
              {formError && <div className="auth-error">{formError}</div>}
              <button className="btn btn-primary btn-block btn-lg" type="submit" disabled={submitting || !resetToken} style={{ marginTop: 24 }}>{submitting ? 'Updating...' : 'Update password'}</button>
            </form>
          )}

          {(sent || mode === 'confirm') && (
            <button className="btn btn-primary btn-block btn-lg" onClick={() => navigate('/login')}>Back to login</button>
          )}
        </div>
      </div>
    </div>
  );
}

function RequireAuth({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.querySelector('[data-main-scroll]')?.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    });
  }, [location.pathname, location.search]);

  return null;
}

function MainApp({ chromeless = false }) {
  const { user, logout } = useAuth();
  const { hydrateContentItem, activeBatches, content } = useContentFeed();
  const location = useLocation();
  const routerNavigate = useNavigate();
  const [screen, setScreen] = useState(() => {
    const routedScreen = screenFromLocation(window.location);
    if (routedScreen) return routedScreen;
    return 'dashboard';
  });
  const [selectedContent, setSelectedContent] = useState(null);
  const [editorBackScreen, setEditorBackScreen] = useState('content');
  const [editorBackOptions, setEditorBackOptions] = useState(null);
  const [editorBackLabel, setEditorBackLabel] = useState('Back');
  const [contentRouteLoading, setContentRouteLoading] = useState(false);
  const [contentRouteError, setContentRouteError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebarState') === 'collapsed');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const [assistantNotificationContext, setAssistantNotificationContext] = useState(null);

  // Density default — theme is controlled by ThemeToggle (localStorage 'dathent-theme').
  // Brand colors come from tokens.css; do not override here.
  useEffect(() => {
    document.documentElement.setAttribute('data-density', 'compact');
  }, []);

  useEffect(() => {
    const path = location.pathname.replace(/^\/app(?=\/|$)/, '') || '/';
    const pathSegments = path.replace(/^\/+/, '').split('/');
    const firstSegment = pathSegments[0];
    if (path.startsWith('/settings/integrations')) {
      if (typeof window !== 'undefined' && (window.localStorage.getItem(ONBOARDING_SOCIAL_RETURN_KEY) || window.localStorage.getItem(ACTIVATION_RETURN_KEY) || window.localStorage.getItem(ASSISTANT_SOCIAL_RETURN_KEY))) return;
      // Preserve OAuth result params (provider, connected, error, accounts, ...)
      // so the Settings screen can show success/failure feedback.
      const params = new URLSearchParams(location.search || '');
      params.set('tab', 'accounts');
      routerNavigate(`/app/settings?${params.toString()}`, { replace: true });
      return;
    }
    const legacySettingsTab = SETTINGS_TAB_FOR_LEGACY_SCREEN[firstSegment];
    if (legacySettingsTab) {
      routerNavigate(`/app/settings?tab=${legacySettingsTab}`, { replace: true });
      return;
    }
    // Map legacy /posts/<x> segments under /app to flat /app/<x>
    if (firstSegment === 'posts' && pathSegments[1]) {
      const sub = pathSegments[1];
      const flatMap = { create: 'create', library: 'library', calendar: 'calendar' };
      if (flatMap[sub]) {
        const search = location.search ? location.search : '';
        routerNavigate(`/app/${flatMap[sub]}${search}`, { replace: true });
        return;
      }
    }
    if (firstSegment === 'brand') {
      routerNavigate(`/app/plan${location.search || ''}`, { replace: true });
    }
  }, [location.pathname]);

  useEffect(() => {
    const routedScreen = screenFromLocation(location);
    if (routedScreen) {
      setScreen(routedScreen);
      return;
    }

    let cancelled = false;
    async function resolveInitialScreen() {
      try {
        const [statusResult, profileResult] = await Promise.allSettled([
          api.onboarding.getStatus(),
          api.profile.getBrandProfile(),
        ]);
        if (cancelled) return;
        const status = statusResult.status === 'fulfilled' ? statusResult.value : null;
        if (status?.onboardingSession?.status === 'in_progress') {
          setScreen('onboarding');
          return;
        }
        setScreen(profileResult.status === 'rejected' ? 'onboarding' : 'dashboard');
      } catch {
        if (!cancelled) setScreen('dashboard');
      }
    }
    resolveInitialScreen();
    return () => {
      cancelled = true;
    };
  }, [location]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(location.search);
    const normalizedPath = location.pathname.replace(/^\/app(?=\/|$)/, '') || '/';
    const hasOAuthResult = params.has('provider') || params.has('connected') || params.has('error');
    const isIntegrationsReturn = normalizedPath.startsWith('/settings/integrations');
    const onboardingReturn = window.localStorage.getItem(ONBOARDING_SOCIAL_RETURN_KEY);
    if (onboardingReturn && isIntegrationsReturn && hasOAuthResult) {
      window.localStorage.removeItem(ONBOARDING_SOCIAL_RETURN_KEY);
      const returnParams = new URLSearchParams({ continue: 'activation' });
      ['provider', 'platform', 'connected', 'accounts', 'error', 'pages', 'instagramBusinessAccounts'].forEach(key => {
        const value = params.get(key);
        if (value) returnParams.set(key, value);
      });
      setScreen('onboarding');
      routerNavigate(`/onboarding?${returnParams.toString()}`, { replace: true });
      return;
    }

    const assistantReturn = window.localStorage.getItem(ASSISTANT_SOCIAL_RETURN_KEY);
    if (assistantReturn && isIntegrationsReturn && hasOAuthResult) {
      window.localStorage.removeItem(ASSISTANT_SOCIAL_RETURN_KEY);
      const returnParams = new URLSearchParams({ tab: 'social', assistant: '1' });
      ['provider', 'platform', 'connected', 'accounts', 'error', 'pages', 'instagramBusinessAccounts'].forEach(key => {
        const value = params.get(key);
        if (value) returnParams.set(key, value);
      });
      setScreen('settings');
      setChatOpen(true);
      routerNavigate(`/settings?${returnParams.toString()}`, { replace: true });
      return;
    }

    const activationReturn = window.localStorage.getItem(ACTIVATION_RETURN_KEY);
    if (!activationReturn || !isIntegrationsReturn || !hasOAuthResult) return;
    window.localStorage.removeItem(ACTIVATION_RETURN_KEY);
    setScreen('preview');
    routerNavigate(SCREEN_PATHS.preview, { replace: true });
  }, [location]);

  useEffect(() => {
    const routeContentId = contentIdFromLocation(location);
    if (!routeContentId) {
      setContentRouteError(null);
      setContentRouteLoading(false);
      return;
    }
    if (selectedContent?.contentItem?.id === routeContentId) {
      setContentRouteError(null);
      setContentRouteLoading(false);
      hydrateContentItem(routeContentId)
        .then(content => {
          if (content) setSelectedContent(current => current?.contentItem?.id === routeContentId ? content : current);
        })
        .catch(() => {});
      return;
    }
    let cancelled = false;
    async function loadRouteContent() {
      setContentRouteLoading(true);
      setContentRouteError(null);
      try {
        const content = await hydrateContentItem(routeContentId);
        if (!cancelled) setSelectedContent(content);
      } catch (err) {
        if (!cancelled) setContentRouteError(err.message || 'Unable to load content item');
      } finally {
        if (!cancelled) setContentRouteLoading(false);
      }
    }
    loadRouteContent();
    return () => {
      cancelled = true;
    };
  }, [location]);

  useEffect(() => {
    if (screen !== 'preview' || contentIdFromLocation(location) || selectedContent) return;
    const raw = sessionStorage.getItem('selected_content');
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.contentItem?.id) setSelectedContent(parsed);
    } catch {
      sessionStorage.removeItem('selected_content');
    }
  }, [screen, location, selectedContent]);

  useEffect(() => {
    let cancelled = false;
    async function loadUnreadCount() {
      try {
        const response = await api.notifications.getUnreadCount();
        if (!cancelled) {
          setNotificationUnreadCount(Number(response.unreadCount || 0));
        }
      } catch {
        if (!cancelled) {
          setNotificationUnreadCount(0);
        }
      }
    }
    loadUnreadCount();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!mobileSidebarOpen) return undefined;
    const scrollY = window.scrollY;
    const previousOverflow = document.body.style.overflow;
    const previousPosition = document.body.style.position;
    const previousTop = document.body.style.top;
    const previousWidth = document.body.style.width;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.position = previousPosition;
      document.body.style.top = previousTop;
      document.body.style.width = previousWidth;
      window.scrollTo(0, scrollY);
    };
  }, [mobileSidebarOpen]);

  const openContentById = (contentId, options = {}) => {
    const normalizedContentId = String(contentId || '').trim();
    if (!normalizedContentId) return false;
    const existing = content.find(item => item.id === normalizedContentId || item.raw?.contentItem?.id === normalizedContentId);
    setEditorBackScreen(options.backScreen || screen || 'content');
    setEditorBackOptions(options.backOptions || null);
    setEditorBackLabel(options.backLabel || 'Back');
    setSelectedContent(existing?.raw || null);
    setContentRouteError(null);
    setContentRouteLoading(!existing?.raw);
    setScreen('preview');
    setMobileSidebarOpen(false);
    setNotificationsOpen(false);
    if (options.keepAssistantOpen) setChatOpen(true);
    routerNavigate(`/content/${encodeURIComponent(normalizedContentId)}/preview`, options.replace ? { replace: true } : undefined);
    return true;
  };

  const navigate = (id, options = {}) => {
    if (id === 'preview' && options.contentId) {
      openContentById(options.contentId, options);
      return;
    }
    if (SETTINGS_TAB_FOR_LEGACY_SCREEN[id]) {
      setScreen('settings');
      setMobileSidebarOpen(false);
      setNotificationsOpen(false);
      routerNavigate(`/settings?tab=${SETTINGS_TAB_FOR_LEGACY_SCREEN[id]}`, options.replace ? { replace: true } : undefined);
      return;
    }
    if (id === 'content') {
      setSelectedContent(null);
      sessionStorage.removeItem('selected_content');
    }
    setScreen(id);
    setMobileSidebarOpen(false);
    setNotificationsOpen(false);
    if (SCREEN_PATHS[id]) {
      const path = SCREEN_PATHS[id];
      const params = new URLSearchParams();
      if (options.focusPostId) params.set('focus', options.focusPostId);
      if (options.batchId) params.set('batch', options.batchId);
      if (options.filter) params.set('filter', options.filter);
      const query = params.toString();
      routerNavigate(query ? `${path}?${query}` : path, options.replace ? { replace: true } : undefined);
    }
  };

  const setDesktopSidebarCollapsed = (collapsed) => {
    setSidebarCollapsed(collapsed);
    localStorage.setItem('sidebarState', collapsed ? 'collapsed' : 'expanded');
  };

  const openMobileSidebar = () => {
    setSidebarCollapsed(false);
    setMobileSidebarOpen(true);
  };

  const completeOnboarding = (destination = 'dashboard') => {
    if (destination === 'firstPostPreview') {
      setScreen('preview');
      routerNavigate(SCREEN_PATHS.preview, { replace: true });
      return;
    }
    if (destination === 'createFirstPost') {
      setScreen('create');
      routerNavigate('/posts/create?source=onboarding', { replace: true });
      return;
    }
    const target = destination === 'brand' || destination === 'brandReview' ? 'brand' : 'dashboard';
    setScreen(target);
    routerNavigate(destination === 'brandReview' ? '/brand?review=onboarding' : SCREEN_PATHS[target], { replace: true });
  };

  const signOut = async () => {
    await logout();
    routerNavigate('/login', { replace: true });
  };

  const openContent = (content, options = {}) => {
    setEditorBackScreen(options.backScreen || screen);
    setEditorBackOptions(options.backOptions || null);
    setEditorBackLabel(options.backLabel || 'Back');
    setSelectedContent(content);
    setScreen('preview');
    const contentId = content?.contentItem?.id;
    routerNavigate(contentId ? `/content/${encodeURIComponent(contentId)}/preview` : SCREEN_PATHS.preview);
  };

  const handleContentGenerated = (content) => {
    setSelectedContent(content);
    openContent(content);
  };

  const editorContentView = (component) => {
    if (contentRouteLoading) {
      return <ContentRouteState icon="refresh" title="Loading content" body="Opening content from the shared link..." />;
    }
    if (contentRouteError) {
      return <ContentRouteState icon="alert" title="Content not available" body={contentRouteError} action={() => navigate('content')} />;
    }
    return component;
  };

  if (screen === 'onboarding') {
    return <Onboarding onComplete={completeOnboarding}/>;
  }

  const titles = {
    dashboard: { t: 'Autopilot', s: 'Status of your AI content engine' },
    scheduler: { t: 'Calendar', s: 'Calendar and queue of upcoming publications' },
    content: { t: 'Library', s: 'All your generated content in one place' },
    create: { t: 'Create post', s: 'Generate content with AI' },
    preview: { t: 'Post preview', s: 'Review post status and next actions' },
    carousel: { t: 'Carousel editor', s: 'Edit your carousel content' },
    editor: { t: 'Edit post', s: 'Refine your post before scheduling' },
    social: { t: 'Social accounts', s: 'Connect and manage your social media accounts' },
    brand: { t: 'Content Plan', s: 'Strategy, brand identity, and per-platform plans' },
    billing: { t: 'Plan & Usage', s: 'Manage plan, usage limits, and subscription' },
    help: { t: 'Help Center', s: 'Answers, setup guides, and support' },
    settings: { t: 'Settings', s: 'Manage your account and preferences' },
    notFound: { t: 'Page not found', s: 'This route does not exist' },
  };

  const showTopbar = screen !== 'notFound';
  const isEditorScreen = screen === 'editor' || screen === 'carousel';
  const assistantSelectedEntity = selectedEntityForAssistant(screen, selectedContent);
  const openAssistantFromNotification = (context) => {
    setAssistantNotificationContext({ ...context, requestId: Date.now() });
    setChatOpen(true);
  };

  // When wrapped by the new AppShell, suppress legacy Sidebar + Topbar chrome.
  // The outer AppShell renders Sidebar + Topbar; we just contribute screen
  // content + overlays (assistant chat, notifications panel).
  useEffect(() => {
    if (!chromeless) return;
    const onOpen = () => setChatOpen(true);
    window.addEventListener('dathent:assistant:open', onOpen);
    return () => window.removeEventListener('dathent:assistant:open', onOpen);
  }, [chromeless]);

  useEffect(() => {
    if (chromeless) return undefined;
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && (event.key === 'k' || event.key === 'K')) {
        event.preventDefault();
        setChatOpen(open => !open);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [chromeless]);

  return (
    <div className={chromeless ? 'app-legacy-content' : ('app app-shell cosmic-bg ' + (sidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'))} data-sidebar={sidebarCollapsed ? 'icons' : 'wide'} data-chat={chatOpen ? 'open' : 'closed'} data-mobile-sidebar={mobileSidebarOpen ? 'open' : 'closed'} data-screen={screen}>
      {!chromeless && (
        <>
          <Sidebar current={screen} onNavigate={navigate} collapsed={sidebarCollapsed} onToggle={() => setDesktopSidebarCollapsed(!sidebarCollapsed)} onExpand={() => setDesktopSidebarCollapsed(false)} user={user} onLogout={signOut} mobileOpen={mobileSidebarOpen} onMobileClose={() => setMobileSidebarOpen(false)}/>
          <div className="sidebar-scrim" onClick={() => setMobileSidebarOpen(false)} />
        </>
      )}
      <main className={chromeless ? '' : 'main'} data-main-scroll>
        {!chromeless && showTopbar && (
          <Topbar
            title={titles[screen]?.t}
            subtitle={isEditorScreen ? null : titles[screen]?.s}
            onMobileMenu={openMobileSidebar}
            navigationMode={['preview', 'editor', 'carousel'].includes(screen) ? 'back' : 'menu'}
            onBack={() => navigate(editorBackScreen || 'content', editorBackOptions || {})}
            onLogoClick={() => navigate('dashboard')}
            actions={
              <>
                {screen === 'content' && <button className="btn btn-primary btn-sm mobile-header-primary" onClick={() => navigate('create')}><Icon name="plus" size={14}/><span>Create</span></button>}
                {!isEditorScreen && screen !== 'content' && <button className="btn btn-icon btn-ghost"><Icon name="search" size={14}/></button>}
                <button className="btn btn-ai" onClick={() => setChatOpen(true)}>
                  <Icon name="sparkles" size={14}/> Ask AI
                </button>
              </>
            }
          />
        )}
        {showTopbar && !isEditorScreen && (
          <ActiveGenerationBanner
            batches={activeBatches}
            content={content}
            currentScreen={screen}
            location={location}
            onResume={batch => navigate('create', { batchId: batch.id })}
            onOpenLibrary={batch => {
              const filter = libraryFilterForBatch(batch, content);
              navigate('content', filter ? { filter } : undefined);
            }}
          />
        )}
        {!showTopbar && (
          <button className="mobile-menu-trigger mobile-menu-floating" onClick={openMobileSidebar} aria-label="Open navigation">
            <Icon name="menu" size={18}/>
          </button>
        )}
        {screen === 'dashboard' && <Dashboard navigate={navigate}/>} 
        {screen === 'scheduler' && <Scheduler navigate={navigate} openPost={openContent}/>} 
        {screen === 'content' && <Content navigate={navigate} openPost={openContent}/>} 
        {screen === 'create' && <Create navigate={navigate} openContent={openContent} onContentGenerated={handleContentGenerated}/>} 
        {screen === 'preview' && editorContentView(<LegacyPostPreview navigate={navigate} content={selectedContent} onContentUpdated={setSelectedContent} backTo={editorBackScreen} backOptions={editorBackOptions} backLabel={editorBackLabel}/>)}
        {screen === 'carousel' && editorContentView(<CarouselEditor navigate={navigate} content={selectedContent} onContentUpdated={setSelectedContent} backTo={editorBackScreen} backOptions={editorBackOptions}/>)} 
        {screen === 'editor' && editorContentView(<PostEditor navigate={navigate} content={selectedContent} onContentUpdated={setSelectedContent} backTo={editorBackScreen} backOptions={editorBackOptions} backLabel={editorBackLabel}/>)} 
        {screen === 'social' && <SocialAccounts/>}
        {screen === 'brand' && <ContentPlanScreen onAskAI={() => setChatOpen(true)}/>}
        {screen === 'billing' && <Billing/>}
        {screen === 'help' && <HelpCenter/>}
        {screen === 'settings' && <Settings/>}
        {screen === 'notFound' && <NotFound onNavigate={navigate}/>} 
      </main>

      {notificationsOpen && (
        <>
          <div className="notif-scrim" onClick={() => setNotificationsOpen(false)} />
          <NotificationsPanel onClose={() => setNotificationsOpen(false)} onNavigate={navigate} onUnreadChange={setNotificationUnreadCount} onOpenAssistant={openAssistantFromNotification}/>
        </>
      )}

      {/* Floating Assistant FAB + panel — only when MainApp is NOT wrapped
          by the new AppShell (which provides its own FAB + GlobalAssistant). */}
      {!chromeless && (
        <>
          <button
            className={'as-fab ' + (chatOpen ? 'is-open' : '')}
            onClick={() => setChatOpen(open => !open)}
            aria-label={chatOpen ? 'Close assistant' : 'Open assistant'}
            title="Dathent Assistant (Ctrl+K)"
          >
            <span className="as-fab-ring"/>
            <span className="as-fab-ring as-fab-ring-2"/>
            <span className="as-fab-ic">
              {chatOpen ? <Icon name="x" size={20}/> : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <defs>
                    <linearGradient id="dathentFabSparkle" x1="0" x2="1" y1="0" y2="1">
                      <stop offset="0%" stopColor="#fff" stopOpacity="1"/>
                      <stop offset="100%" stopColor="#fff" stopOpacity="0.85"/>
                    </linearGradient>
                  </defs>
                  <path d="M12 3 13.5 8 18 9.5 13.5 11 12 16 10.5 11 6 9.5 10.5 8z" fill="url(#dathentFabSparkle)"/>
                  <path d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8z" fill="#fff" opacity="0.9"/>
                </svg>
              )}
            </span>
            {!chatOpen && <span className="as-fab-badge"/>}
          </button>

          <AIPanel
            open={chatOpen}
            onClose={() => setChatOpen(false)}
            currentScreen={screen}
            selectedEntity={assistantSelectedEntity}
            notificationContext={assistantNotificationContext}
            onVoice={() => setVoiceOpen(true)}
            navigate={navigate}
          />

          {voiceOpen && <VoiceMode onClose={() => setVoiceOpen(false)}/>}
        </>
      )}
    </div>
  );
}

function selectedEntityForAssistant(screen, selectedContent) {
  if (['preview', 'editor', 'carousel'].includes(screen)) {
    return {
      type: 'contentItem',
      id: selectedContent?.contentItem?.id || selectedContent?.id || null,
    };
  }
  if (screen === 'brand') return { type: 'brandProfile', id: null };
  if (screen === 'billing') return { type: 'billingAccount', id: null };
  return null;
}

const ACTIVE_GENERATION_STATUSES = new Set(['queued', 'generating', 'reserved']);

function ActiveGenerationBanner({ batches = [], content = [], currentScreen, location, onResume, onOpenLibrary }) {
  const currentBatchId = new URLSearchParams(location.search).get('batch');
  const visibleBatches = (batches || [])
    .filter(isActiveGenerationBatch)
    .filter(batch => !(currentScreen === 'create' && batch.id === currentBatchId))
    .sort((a, b) => Date.parse(b.createdAt || 0) - Date.parse(a.createdAt || 0));

  if (!visibleBatches.length) return null;

  const batch = visibleBatches[0];
  const batchItems = content.filter(item => item.generationBatchId === batch.id);
  const total = Math.max(1, Number(batch.totalItems || 0), batchItems.length);
  const readyCount = Math.max(
    Number(batch.readyCount || 0),
    batchItems.filter(item => ['ready', 'scheduled', 'publishing', 'published'].includes(item.status)).length,
  );
  const failedCount = Math.max(Number(batch.failedCount || 0), batchItems.filter(item => item.status === 'failed').length);
  const progress = generationProgressForBatch(batch, batchItems, total);
  const moreCount = visibleBatches.length - 1;
  const topic = batch.topic || batchItems[0]?.title || 'Generating content';

  return (
    <div className="global-generation-banner" role="status" aria-live="polite">
      <div className="global-generation-orb"><Icon name="sparkles" size={15} /></div>
      <div className="global-generation-main">
        <div className="global-generation-row">
          <div className="global-generation-copy">
            <div className="global-generation-title">Generating: {topic}</div>
            <div className="global-generation-meta">
              {readyCount}/{total} ready
              {failedCount > 0 ? ` · ${failedCount} failed` : ''}
              {moreCount > 0 ? ` · ${moreCount} more running` : ''}
            </div>
          </div>
          <div className="global-generation-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => onResume(batch)}><Icon name="play" size={12} /> Resume preview</button>
            <button className="btn btn-ghost btn-sm" onClick={() => onOpenLibrary(batch)}><Icon name="grid" size={12} /> Open Library</button>
          </div>
        </div>
        <div className="global-generation-progress"><div style={{ width: `${progress}%` }} /></div>
      </div>
    </div>
  );
}

function isActiveGenerationBatch(batch) {
  return batch?.id && ACTIVE_GENERATION_STATUSES.has(batch.status);
}

function generationProgressForBatch(batch, items, total) {
  const batchProgress = Number(batch?.progress);
  const done = (items || []).filter(item => ['ready', 'scheduled', 'publishing', 'published', 'failed'].includes(item.status)).length;
  const rowProgress = Math.round((done / Math.max(1, total)) * 100);
  const progress = Number.isFinite(batchProgress) && batchProgress > 0 ? Math.max(batchProgress, rowProgress) : rowProgress;
  return Math.max(5, Math.min(100, Math.round(progress)));
}

const LIBRARY_FILTER_STATUSES = new Set(['generating', 'ready', 'scheduled', 'published', 'draft', 'failed']);

function libraryFilterForBatch(batch, content) {
  const items = content.filter(item => item.generationBatchId === batch.id);
  const filter = libraryFilterForStatuses(items.map(item => item.status));
  if (filter) return filter;
  if (!items.length && isActiveGenerationBatch(batch)) return 'generating';
  return null;
}

function libraryFilterForStatuses(statuses) {
  const filters = [...new Set(statuses.map(normalizeLibraryFilterStatus).filter(Boolean))];
  return filters.length === 1 ? filters[0] : null;
}

function normalizeLibraryFilterStatus(status) {
  return LIBRARY_FILTER_STATUSES.has(status) ? status : null;
}

function ContentRouteState({ icon, title, body, action }) {
  return (
    <div className="page" style={{ maxWidth: 720 }}>
      <div className="card content-empty-v2">
        <Icon name={icon} size={24} />
        <h2 className="h2" style={{ margin: '12px 0 8px' }}>{title}</h2>
        <p className="body" style={{ color: 'var(--text-3)', marginBottom: action ? 18 : 0 }}>{body}</p>
        {action && <button className="btn btn-primary" onClick={action}>Back to content</button>}
      </div>
    </div>
  );
}

export default function AppRoot() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/login" element={<NewAuth mode="login" />} />
        <Route path="/signup" element={<NewAuth mode="signup" />} />
        <Route path="/forgot" element={<NewAuth mode="forgot" />} />
        <Route path="/reset" element={<NewAuth mode="reset" />} />
        <Route path="/confirm" element={<NewAuth mode="confirm" />} />

        {/* Root → new AppShell dashboard. Without this, "/" fell into the
            /* catch-all below and rendered the LEGACY MainApp chrome
            (old sidebar + "Ask AI" topbar). */}
        <Route path="/" element={<Navigate to="/app" replace />} />

        {/* Redirects from legacy paths → /app/* (pack canonical) */}
        <Route path="/dashboard" element={<Navigate to="/app" replace />} />
        <Route path="/posts/create" element={<Navigate to="/app/create" replace />} />
        <Route path="/posts/library" element={<Navigate to="/app/library" replace />} />
        <Route path="/posts/calendar" element={<Navigate to="/app/calendar" replace />} />
        <Route path="/content-plan" element={<Navigate to="/app/plan" replace />} />
        <Route path="/brand" element={<Navigate to="/app/plan" replace />} />
        <Route path="/settings" element={<Navigate to="/app/settings" replace />} />
        <Route path="/social" element={<Navigate to="/app/settings?tab=social" replace />} />
        <Route path="/billing" element={<Navigate to="/app/settings?tab=billing" replace />} />
        <Route path="/help" element={<Navigate to="/app/help" replace />} />
        <Route path="/preview" element={<Navigate to="/app/preview" replace />} />

        {/* /app — nested: index → new Dashboard via AppShell, /app/* → MainApp for un-ported screens */}
        <Route path="/app" element={
          <RequireAuth>
            <ContentFeedProvider>
              <DashboardDataProvider>
                <Outlet />
              </DashboardDataProvider>
            </ContentFeedProvider>
          </RequireAuth>
        }>
          <Route index element={<AppShell topbarTitle="Dashboard"><Dashboard /></AppShell>} />
          <Route path="library" element={<AppShell topbarTitle="Library"><Library /></AppShell>} />
          <Route path="post/:id" element={<AppShell topbarTitle="Post"><PostPreview /></AppShell>} />
          <Route path="post/:id/edit" element={<AppShell topbarTitle="Edit post"><EditPost /></AppShell>} />
          <Route path="settings" element={<AppShell topbarTitle="Settings"><NewSettings /></AppShell>} />
          <Route path="create" element={<AppShell topbarTitle="Create posts"><NewCreatePost /></AppShell>} />
          <Route path="plan" element={<AppShell topbarTitle="Content Plan"><NewContentPlan /></AppShell>} />
          <Route path="plan/edit-strategy" element={<AppShell topbarTitle="Edit Strategy"><NewEditStrategy /></AppShell>} />
          {/* Legacy URL guard — any stray /app/content-plan link (from older
              code or shared bookmarks) bounces to the new /app/plan page
              instead of falling into the MainApp catch-all and showing
              the legacy ContentPlan screen. */}
          <Route path="content-plan" element={<Navigate to="/app/plan" replace />} />
          <Route path="content-plan/*" element={<Navigate to="/app/plan" replace />} />
          <Route path="calendar" element={<AppShell topbarTitle="Calendar"><NewCalendar /></AppShell>} />
          <Route path="messages" element={<AppShell topbarTitle="Messages"><NewMessages /></AppShell>} />
          <Route path="batch/:id" element={<AppShell topbarTitle="Batch progress"><NewBatch /></AppShell>} />
          <Route path="referrals" element={<AppShell topbarTitle="Referrals" topbarSub="settings / referrals"><NewReferrals /></AppShell>} />
          <Route path="admin/referrals" element={<AppShell topbarTitle="Admin · Referrals" topbarSub="admin / referrals"><AdminReferrals /></AppShell>} />
          {/* All un-ported MainApp screens wrap in AppShell — internal Sidebar/Topbar
              suppressed via chromeless prop so the new chrome is the only one shown. */}
          <Route path="*" element={<AppShell><MainApp chromeless /></AppShell>} />
        </Route>

        {/* Public referral entry points — no AppShell, no auth */}
        <Route path="/r/:code/welcome" element={<ReferralWelcome />} />
        <Route path="/r/:code" element={<ReferralSignup />} />

        <Route path="/*" element={<RequireAuth><ContentFeedProvider><DashboardDataProvider><MainApp /></DashboardDataProvider></ContentFeedProvider></RequireAuth>} />
      </Routes>
    </BrowserRouter>
  );
}
